import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import LicenseExpiredBanner from '../components/LicenseExpiredBanner';
import PlatformInstallGuide, { type TokenError } from '../components/PlatformInstallGuide';

interface Agent {
	agent_id: string;
	user_id: string;
	hostname: string;
	platform: string;
	version: string;
	status: 'online' | 'offline';
	last_heartbeat: number;
	created_at: number;
	local_ips?: string | null;
	last_ip?: string | null;
	latest_version?: string;
	update_available?: boolean;
	running_as_root?: number | null;
}

interface BotInstance {
	instance_id: string;
	agent_id: string;
	clawdbot_version: string;
	install_path: string;
	running_status: 'running' | 'stopped' | 'error';
}

export default function MachinesPage() {
	const { firebaseUser, user } = useAuth();
	const { currentTeam } = useTeam();
	const { getHeaders } = useApiHeaders();
	const [agents, setAgents] = useState<Agent[]>([]);
	const [instances, setInstances] = useState<BotInstance[]>([]);
	const [loading, setLoading] = useState(true);
	const [showInstallGuide, setShowInstallGuide] = useState(false);
	const [registrationToken, setRegistrationToken] = useState<string | null>(null);
	const [tokenLoading, setTokenLoading] = useState(true);
	const [tokenError, setTokenError] = useState<TokenError | null>(null);

	// Fetch agents
	useEffect(() => {
		let cancelled = false;

		const fetchAgents = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/agents`, {
					headers: headers,
				});

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json();
					setAgents(data.agents || []);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching agents:', error);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchAgents();
		const interval = setInterval(fetchAgents, 30000);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [firebaseUser, currentTeam]);

	// Fetch bot instances
	useEffect(() => {
		let cancelled = false;

		const fetchInstances = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/instances`, {
					headers: headers,
				});

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json();
					setInstances(data.instances || []);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching instances:', error);
			}
		};

		fetchInstances();
		const interval = setInterval(fetchInstances, 30000);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [firebaseUser, currentTeam]);

	// Fetch registration token
	useEffect(() => {
		let cancelled = false;

		const fetchToken = async () => {
			if (!firebaseUser) {
				setTokenLoading(false);
				return;
			}

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/agents/token?server_url=${encodeURIComponent(API_URL)}`, {
					headers: headers,
				});

				if (cancelled) return;

				if (response.ok) {
					const data = await response.json();
					setRegistrationToken(data.token);
					setTokenError(null);
				} else if (response.status === 403) {
					const errorData = await response.json().catch(() => ({ error: 'Forbidden', message: 'Access denied' }));
					setTokenError(errorData);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching token:', error);
			} finally {
				if (!cancelled) setTokenLoading(false);
			}
		};

		fetchToken();
		return () => { cancelled = true; };
	}, [firebaseUser, currentTeam]);

	const getInstancesForAgent = (agentId: string) => {
		return instances.filter(i => i.agent_id === agentId);
	};

	const getRunningCount = (agentId: string) => {
		return getInstancesForAgent(agentId).filter(i => i.running_status === 'running').length;
	};

	const formatLastSeen = (timestamp: number) => {
		const diff = Date.now() - timestamp;
		if (diff < 60000) return 'Just now';
		if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
		if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
		return `${Math.floor(diff / 86400000)}d ago`;
	};

	const getPlatformIcon = (platform: string) => {
		const p = platform.toLowerCase();
		if (p.includes('darwin') || p.includes('macos')) {
			return (
				<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
					<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
				</svg>
			);
		}
		if (p.includes('windows')) {
			return (
				<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
					<path d="M3 12V6.75l8-1.25V12H3zm0 .5h8v6.5l-8-1.25V12.5zM11.5 5.34l9.5-1.34v8h-9.5V5.34zM11.5 12.5H21v7.84l-9.5-1.34V12.5z"/>
				</svg>
			);
		}
		// Linux
		return (
			<svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
				<path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.065 1.326-.53 1.441-.813.188-.39.025-.975-.254-1.603-.267-.585-.61-1.135-.529-1.404l.003-.003c.458-.396.91-1.069 1.148-1.6.009-.065.014-.129.014-.193 0-.275-.114-.557-.378-.756-.128-.097-.276-.159-.418-.202.108-.39.105-.765.012-1.12-.108-.41-.296-.774-.496-1.095-.401-.599-.936-1.254-.96-2.284-.026-.665.06-1.297.236-1.867a5.79 5.79 0 00-.14-.849c-.39-1.899-1.39-3.064-2.333-4.167-.945-1.103-1.98-2.163-2.15-3.835-.084-.843-.009-1.715.174-2.475C14.324.727 13.663.003 12.504 0z"/>
			</svg>
		);
	};

	const agentsNeedingUpdate = agents.filter(a => a.update_available && a.status === 'online');
	const agentsRunningAsRoot = agents.filter(a => a.running_as_root === 1 && a.status === 'online');

	const handleUpdateAgent = async (agentId: string, version: string) => {
		try {
			const headers = await getHeaders();
			await fetch(`${API_URL}/api/v1/agents/${agentId}/update`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ version }),
			});
		} catch (error) {
			console.error('Failed to update agent:', error);
		}
	};

	const handleUpdateAll = async () => {
		for (const agent of agentsNeedingUpdate) {
			await handleUpdateAgent(agent.agent_id, agent.latest_version || 'latest');
		}
	};

	if (loading) {
		return (
			<>
				<LicenseExpiredBanner />
				<Layout title="Devices" subtitle="Computers connected to Clawdgate via the Connector">
					<div className="flex items-center justify-center h-64">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
					</div>
				</Layout>
			</>
		);
	}

	return (
		<>
			<LicenseExpiredBanner />
			<Layout title="Devices" subtitle="Computers connected to Clawdgate via the Connector">
			{/* Running-as-root Banner — fleet-level summary, drill into the
			    device for the copy-paste migration command. */}
			{agentsRunningAsRoot.length > 0 && (
				<div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-xl p-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-red-100 dark:bg-red-800/40 rounded-lg shrink-0">
							<svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
							</svg>
						</div>
						<div className="flex-1 min-w-0">
							<p className="font-semibold text-red-900 dark:text-red-200">
								{agentsRunningAsRoot.length === 1
									? `${agentsRunningAsRoot[0].hostname} is running as root`
									: `${agentsRunningAsRoot.length} devices are running as root`}
							</p>
							<p className="text-sm text-red-700 dark:text-red-300">
								Legacy install path. Click the device for migration instructions
								(no remote access needed — runs locally on the device).
							</p>
						</div>
					</div>
				</div>
			)}

			{/* Upgrade Available Banner */}
			{agentsNeedingUpdate.length > 0 && (
				<div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<div className="p-2 bg-amber-100 dark:bg-amber-800/40 rounded-lg">
								<svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
								</svg>
							</div>
							<div>
								<p className="font-semibold text-amber-800 dark:text-amber-300">
									Connector Update Available
								</p>
								<p className="text-sm text-amber-700 dark:text-amber-400">
									{agentsNeedingUpdate.length === 1
										? `${agentsNeedingUpdate[0].hostname} can be upgraded to v${agentsNeedingUpdate[0].latest_version}`
										: `${agentsNeedingUpdate.length} machines have updates available (v${agentsNeedingUpdate[0].latest_version})`
									}
								</p>
							</div>
						</div>
						<button
							onClick={handleUpdateAll}
							className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors whitespace-nowrap"
						>
							{agentsNeedingUpdate.length === 1 ? 'Update Now' : `Update All (${agentsNeedingUpdate.length})`}
						</button>
					</div>
				</div>
			)}

			{/* Stats */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm text-slate-600 dark:text-slate-400">Total Machines</p>
							<p className="text-3xl font-bold text-slate-900 dark:text-white">{agents.length}</p>
						</div>
						<div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-xl">
							<svg className="w-6 h-6 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
							</svg>
						</div>
					</div>
				</div>

				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm text-slate-600 dark:text-slate-400">Online</p>
							<p className="text-3xl font-bold text-green-600 dark:text-green-400">{agents.filter(a => a.status === 'online').length}</p>
						</div>
						<div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
							<svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						</div>
					</div>
				</div>

				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm text-slate-600 dark:text-slate-400">AI Agents</p>
							<p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{instances.length}</p>
						</div>
						<div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
							<svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
							</svg>
						</div>
					</div>
				</div>
			</div>

			{/* Add Machine Button */}
			<div className="flex justify-between items-center mb-6">
				<h2 className="text-xl font-semibold text-slate-900 dark:text-white">Your Machines</h2>
				<button
					onClick={() => setShowInstallGuide(!showInstallGuide)}
					className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer flex items-center space-x-2"
				>
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
					</svg>
					<span>Add Machine</span>
				</button>
			</div>

			{/* Install Guide */}
						{showInstallGuide && (
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-6">
					<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Install Agent on New Machine</h3>
					<PlatformInstallGuide
						registrationToken={registrationToken}
						tokenLoading={tokenLoading}
						tokenError={tokenError}
						compact
					/>
				</div>
			)}

			{/* Machines List */}
			{agents.length === 0 ? (
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center">
					<div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl mb-4">
						<svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
						</svg>
					</div>
					<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No machines connected</h3>
					<p className="text-slate-600 dark:text-slate-400 mb-4">Install the agent on your first machine to get started</p>
					<button
						onClick={() => setShowInstallGuide(true)}
						className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-xl font-medium cursor-pointer"
					>
						Add Your First Machine
					</button>
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{agents.map((agent) => {
						const agentInstances = getInstancesForAgent(agent.agent_id);
						const runningCount = getRunningCount(agent.agent_id);

						return (
							<Link
								key={agent.agent_id}
								to={`/machines/${agent.agent_id}`}
								className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 hover:shadow-lg hover:border-primary-300 dark:hover:border-primary-700 transition-all duration-200 cursor-pointer group"
							>
								<div className="flex items-start justify-between mb-4">
									<div className="flex items-center space-x-3">
										<div className={`p-2 rounded-xl ${agent.status === 'online' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
											{getPlatformIcon(agent.platform)}
										</div>
										<div>
											<div className="flex items-center space-x-2">
												<h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
													{agent.hostname}
												</h3>
												{agent.user_id !== user?.uid && (
													<span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
														<svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
														</svg>
														Shared
													</span>
												)}
											</div>
											<p className="text-sm text-slate-500 dark:text-slate-400">
												{agent.platform} • v{agent.version}
												{agent.update_available && (
													<span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
														v{agent.latest_version} available
													</span>
												)}
												{(() => {
													const ip = (() => {
														try {
															const ips: string[] = agent.local_ips ? JSON.parse(agent.local_ips) : [];
															return ips[0] || agent.last_ip;
														} catch {
															return agent.last_ip;
														}
													})();
													return ip ? ` • ${ip}` : null;
												})()}
											</p>
										</div>
									</div>
									<div className={`w-3 h-3 rounded-full ${agent.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-slate-400'}`} />
								</div>

								<div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
									<div className="flex items-center space-x-4">
										<div className="text-center">
											<p className="text-2xl font-bold text-slate-900 dark:text-white">{agentInstances.length}</p>
											<p className="text-xs text-slate-500 dark:text-slate-400">AI Agents</p>
										</div>
										{agentInstances.length > 0 && (
											<div className="text-center">
												<p className="text-2xl font-bold text-green-600 dark:text-green-400">{runningCount}</p>
												<p className="text-xs text-slate-500 dark:text-slate-400">Running</p>
											</div>
										)}
									</div>
									<div className="text-right">
										<p className="text-xs text-slate-500 dark:text-slate-400">Last seen</p>
										<p className="text-sm font-medium text-slate-700 dark:text-slate-300">
											{agent.status === 'online' ? 'Now' : formatLastSeen(agent.last_heartbeat)}
										</p>
									</div>
								</div>

								<div className="mt-4 flex items-center text-sm text-primary-600 dark:text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity">
									<span>Manage machine</span>
									<svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
									</svg>
								</div>
							</Link>
						);
					})}
				</div>
			)}
		</Layout>
		</>
	);
}
